import { Box, Menu, MenuItem, FormControl, InputLabel, Select } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Button, ContainerPage, TitlePage } from 'components'
import { DownloadExcelAnalysis } from 'components/ModalDownloadExcelDataAnalysis'
import React, { useState, MouseEvent, useEffect, useRef } from 'react'
import { RiFileDownloadLine, RiArrowDownSLine } from 'react-icons/ri'

import { ISelectDate } from '../../../components/DateRangerPicker'
import { usePopup } from '../../../hooks/usePopup'
import { ISeparationGroupConfig } from '../../../interfaces/ISeparationGroupService'
import { settingsService } from '../../../services/settingsService'
import AverageTimeItem from './AverageTimeItem'
import AverageTimeOrder from './AverageTimeOrder'
import BreakReport from './BreakReport'
import RefreshControl from './components/RefreshControl'
import SeparationMethodFilter from './components/SeparationMethodFilter'
import DateRangerPicker from './components/DateRangerPicker'

const useStyles = makeStyles(theme => ({
  filtersContainer: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '1rem',
    marginBottom: '1rem',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      '& > *': {
        width: '100% !important',
        minWidth: 'unset !important'
      }
    }
  },
  filterButton: {
    minWidth: 250,
    height: '40px',
    [theme.breakpoints.down('md')]: {
      width: '100%'
    }
  },
  filterControl: {
    minWidth: 200,
    height: '40px',
    [theme.breakpoints.down('md')]: {
      width: '100%'
    }
  },
  downloadButton: {
    width: '11rem',
    height: '40px'
  }
}))

const getYesterday = () => {
  const date = new Date()
  const hour = date.getHours()
  date.setDate(date.getDate() - (hour >= 0 && hour < 12 ? 2 : 1))
  return date
}

const Performance = () => {
  const classes = useStyles()
  const [tab, setTab] = useState(0)
  const [openModalExcel, setOpenModalExcel] = useState(false)
  const { addPopup } = usePopup()
  const [groups, setGroups] = useState<ISeparationGroupConfig[]>([])
  const [selectedGroup, setSelectedGroup] = useState<number | undefined>(undefined)
  const [selectedSeparationMethod, setSelectedSeparationMethod] = useState<string>('')
  const [selectedDate, setSelectedDate] = useState({
    from: getYesterday(),
    to: getYesterday(),
  })
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const [refreshInterval, setRefreshInterval] = useState<number>(15000)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const reportTypes = [
    'Média de separação / pedido',
    'Média de separação / item',
    'Total de quebras',
  ]

  const handleMenuClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleMenuItemClick = (index: number) => {
    setTab(index)
    setAnchorEl(null)
  }

  const handleOpenModalExcel = () => {
    setOpenModalExcel(state => !state)
  }

  const handleCloseModalExcel = () => {
    setOpenModalExcel(false)
  }

  const onFilter = (params: ISelectDate) => {
    setSelectedDate({
      from: params.startDate,
      to: params.endDate,
    })
  }

  const handleGroupChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setSelectedGroup(
      (event.target.value as string) === 'undefined'
        ? undefined
        : parseInt(event.target.value as string),
    )
  }

  const handleRefreshNow = () => {
    setRefreshTrigger(prev => prev + 1)
  }

  const handleIntervalChange = (interval: number) => {
    setRefreshInterval(interval)
  }

  const startAutoRefresh = () => {
    // Limpar intervalo anterior se existir
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }

    // Só criar intervalo se não estiver desligado (0)
    if (refreshInterval > 0) {
      intervalRef.current = setInterval(() => {
        setRefreshTrigger(prev => prev + 1)
      }, refreshInterval)
    }
  }

  const stopAutoRefresh = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }

  const getGroups = async () => {
    try {
      const response = await settingsService.getSeparationGroup()
      setGroups(response)
    } catch {
      addPopup({
        title: 'Erro ao buscar grupos de separação',
        type: 'error',
      })
    }
  }

  const getCurrent = async () => {
    try {
      const response = await settingsService.getCurrent()
      const localDate = new Date(response.scheduled_date + 'T00:00:00')
      setSelectedDate({
        from: localDate,
        to: localDate,
      })
    } catch {}
  }


  useEffect(() => {
    getGroups()
    getCurrent()
    startAutoRefresh()

    // Cleanup function para limpar o intervalo quando o componente for desmontado
    return () => {
      stopAutoRefresh()
    }
  }, [])

  // Reiniciar auto-refresh quando mudar de aba, data, grupo, método de separação ou intervalo
  useEffect(() => {
    startAutoRefresh()
    return () => {
      stopAutoRefresh()
    }
  }, [tab, selectedDate, selectedGroup, selectedSeparationMethod, refreshInterval])


  return (
    <>
      <ContainerPage>
        <TitlePage>Desempenho dos separadores</TitlePage>
        <Box>
          <Box className={classes.filtersContainer}>
            <Button
              variant="outlined"
              onClick={handleMenuClick}
              endIcon={<RiArrowDownSLine />}
              className={classes.filterButton}
              style={{
                justifyContent: 'space-between',
                background: '#fff',
                padding: '0.4rem 1rem',
              }}
            >
              {reportTypes[tab]}
            </Button>
            <DateRangerPicker
              startDate={selectedDate.from}
              endDate={selectedDate.to}
              onSelectedDate={onFilter}
              title="Período de entrega"
              isScheduledDate
            />
            {[0,1].includes(tab) && (
              <FormControl 
                variant="outlined" 
                size="small" 
                className={classes.filterControl}
              >
                <InputLabel id="group-select-label">Grupo de separação</InputLabel>
                <Select
                  labelId="group-select-label"
                  value={String(selectedGroup)}
                  onChange={handleGroupChange}
                  label="Grupo de separação"
                  style={{ 
                    background: '#fff'
                  }}
                >
                  <MenuItem value="undefined">
                    <em>Todos os grupos</em>
                  </MenuItem>
                  {groups.map(group => (
                    <MenuItem key={group.separation_group} value={group.separation_group}>
                      Grupo {group.separation_group}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {tab === 1 && (
              <SeparationMethodFilter
                value={selectedSeparationMethod}
                onChange={setSelectedSeparationMethod}
                disabled={false}
              />
            )}

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              getContentAnchorEl={null}
              style={{ marginTop: 1 }}
            >
              {reportTypes.map((type, index) => (
                <MenuItem
                  key={index}
                  onClick={() => handleMenuItemClick(index)}
                  selected={index === tab}
                >
                  {type}
                </MenuItem>
              ))}
            </Menu>

            <Box style={{ marginLeft: 'auto' }}>
              <RefreshControl
                onRefresh={handleRefreshNow}
                onIntervalChange={handleIntervalChange}
                defaultInterval={refreshInterval}
              />
            </Box>

            <Button
              className={classes.downloadButton}
              onClick={handleOpenModalExcel}
              variant="contained"
              startIcon={<RiFileDownloadLine size={20} />}
            >
              Baixar planilha
            </Button>
          </Box>
          {tab === 0 && (
            <AverageTimeOrder
              scheduledDateGte={selectedDate.from}
              scheduledDateLte={selectedDate.to}
              selectedGroupId={selectedGroup}
              refreshTrigger={refreshTrigger}
            />
          )}
          {tab === 1 && (
            <AverageTimeItem
              scheduledDateGte={selectedDate.from}
              scheduledDateLte={selectedDate.to}
              selectedGroupId={selectedGroup}
              refreshTrigger={refreshTrigger}
              separationMethod={selectedSeparationMethod}
            />
          )}
          {tab === 2 && (
            <BreakReport
              handleOpenModalExcel={handleOpenModalExcel}
              initialSeparationDate={selectedDate.from}
              finalSeparationDate={selectedDate.to}
              refreshTrigger={refreshTrigger}
            />
          )}
        </Box>
      </ContainerPage>

      <DownloadExcelAnalysis isOpen={openModalExcel} handleCloseModal={handleCloseModalExcel} />
    </>
  )
}

export default Performance
